import { useEffect, useMemo, useRef } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../hooks/useJobs";
import type { Job } from "../../../utils/models";
import type { RoleSection } from "../WeeklyGapView/useWeeklyGapView";
import { resolveSectionJob } from "./gapsGroupSheets";

/** One job section two or more enabled jobs share a display name with. */
export type GapsAmbiguousJobSection = { jobName: string; jobIds: string[] };

/**
 * The job names more than one ENABLED job carries among the week's sections, in section order — `resolveSectionJob`'s
 * own rule. Once per name: the gap view builds a section per enabled job, so two jobs named "Aide" are two sections
 * named "Aide", and one finding.
 */
export const ambiguousJobSections = (sections: readonly RoleSection[], enabledJobs: readonly Job[]): GapsAmbiguousJobSection[] => {
	const byName = new Map<string, GapsAmbiguousJobSection>();
	for (const section of sections) {
		const { ambiguousJobIds } = resolveSectionJob(section, enabledJobs);
		if (ambiguousJobIds !== null && !byName.has(section.shortRole)) byName.set(section.shortRole, { jobName: section.shortRole, jobIds: ambiguousJobIds });
	}
	return Array.from(byName.values());
};

/**
 * Reports, through the app's surfaced-error path (`trackError`), a department whose enabled jobs share a display name —
 * the configuration that leaves a gaps-home job section unable to say which job it is (`GapsSectionScope.ambiguousJobIds`).
 *
 * The home itself keeps working and says so on the section; this is how the misconfiguration reaches us rather than
 * only the person looking at it. Once per distinct finding for the mounted home, not per render: the sections are
 * rebuilt on every data refresh, and the same finding again is noise.
 */
export const useReportAmbiguousJobSections = (sections: readonly RoleSection[]): void => {
	const { trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const enabledJobs = useJobs();
	const found = useMemo(() => ambiguousJobSections(sections, enabledJobs), [sections, enabledJobs]);
	// The finding by value: a rebuilt `sections` that is ambiguous in the same way is the same finding.
	const findingKey = found.map((section) => `${section.jobName}:${section.jobIds.join(",")}`).join("|");
	const reportedKeyRef = useRef<string>("");
	const departmentId = chosenDepartment.department_id;

	useEffect(() => {
		if (findingKey === reportedKeyRef.current) return;
		reportedKeyRef.current = findingKey;
		for (const section of found) {
			trackError("gaps_ambiguous_job_section", {
				department_id: departmentId,
				job_name: section.jobName,
				job_ids: section.jobIds.join(","),
				error_message: `Enabled jobs ${section.jobIds.join(", ")} share the display name "${section.jobName}": the gaps home cannot tell its section's job apart, so post and add are off for it`,
			});
		}
	}, [found, findingKey, departmentId, trackError]);
};
